import { useState, useEffect } from "react";
import axios from "axios";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DollarSign,
  Loader2,
  TrendingUp,
  CheckCircle,
  Clock,
  Percent,
  BookOpen
} from "lucide-react";
import PayoutLedgerDialog from "@/components/payouts/PayoutLedgerDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PAYOUT_STATUS_STYLE = {
  pending: "bg-yellow-100 text-yellow-700",
  processing: "bg-blue-100 text-blue-700",
  completed: "bg-green-100 text-green-700",
  failed: "bg-red-100 text-red-700",
  cancelled: "bg-slate-100 text-slate-600",
};

export default function DistributorEarnings({ distributor }) {
  const [earnings, setEarnings] = useState([]);
  const [summary, setSummary] = useState(null);
  const [payouts, setPayouts] = useState([]);
  const [ledgerPayoutId, setLedgerPayoutId] = useState(null);
  const [loading, setLoading] = useState(true);

  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem("session_token")}` }
  });

  useEffect(() => {
    if (distributor) {
      fetchEarnings();
    }
  }, [distributor]);

  const fetchEarnings = async () => {
    try {
      setLoading(true);
      const [earningsRes, statsRes] = await Promise.all([
        axios.get(`${API}/distributor/earnings`, getAuthHeaders()),
        axios.get(`${API}/distributor/stats`, getAuthHeaders())
      ]);
      setEarnings(earningsRes.data || []);
      setSummary(statsRes.data);
    } catch (error) {
      console.error("Failed to fetch earnings:", error);
    }

    // Payout history is its own request so a failure here can't blank the earnings above.
    try {
      const payoutsRes = await axios.get(`${API}/distributor/payouts`, getAuthHeaders());
      setPayouts(payoutsRes.data || []);
    } catch (error) {
      console.error("Failed to fetch payouts:", error);
      setPayouts([]);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 text-purple-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="distributor-earnings">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-900">
          Earnings
        </h1>
        <p className="text-slate-500">Your commission earnings breakdown</p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border p-5">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
              <Percent className="w-5 h-5 text-purple-600" />
            </div>
            <span className="text-sm text-slate-500">Commission Rate</span>
          </div>
          <p className="text-2xl font-bold text-slate-900">
            {distributor?.commission_percent || 10}%
          </p>
        </div>
        
        <div className="bg-white rounded-xl border p-5">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
              <DollarSign className="w-5 h-5 text-green-600" />
            </div>
            <span className="text-sm text-slate-500">Total Earned</span>
          </div>
          <p className="text-2xl font-bold text-green-600">
            ${(summary?.total_earnings || 0).toFixed(2)}
          </p>
        </div>
        
        <div className="bg-white rounded-xl border p-5">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
              <Clock className="w-5 h-5 text-amber-600" />
            </div>
            <span className="text-sm text-slate-500">Pending</span>
          </div>
          <p className="text-2xl font-bold text-amber-600">
            ${(summary?.pending_earnings || 0).toFixed(2)}
          </p>
          {(summary?.in_payout || 0) > 0 && (
            <p className="text-xs text-blue-600 mt-1">
              + ${summary.in_payout.toFixed(2)} in a payout being processed
            </p>
          )}
        </div>
        
        <div className="bg-white rounded-xl border p-5">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <CheckCircle className="w-5 h-5 text-blue-600" />
            </div>
            <span className="text-sm text-slate-500">Paid Out</span>
          </div>
          <p className="text-2xl font-bold text-blue-600">
            ${(summary?.paid_earnings || 0).toFixed(2)}
          </p>
        </div>
      </div>

      {/* Payout History */}
      {payouts.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border overflow-hidden" data-testid="distributor-payout-history">
          <div className="p-4 border-b">
            <h2 className="font-semibold text-slate-900">Payout History</h2>
            <p className="text-sm text-slate-500">Payments made to you, and the ledger entries behind each one</p>
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead className="text-right">Amount</TableHead>
                <TableHead className="text-center">Status</TableHead>
                <TableHead>Details</TableHead>
                <TableHead className="text-right">Ledger</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {payouts.map((payout) => (
                <TableRow key={payout.payout_id}>
                  <TableCell className="text-sm text-slate-500">
                    {new Date(payout.created_at).toLocaleDateString()}
                    {payout.processed_at && (
                      <span className="block text-xs">Paid {new Date(payout.processed_at).toLocaleDateString()}</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right font-medium text-red-600">
                    −${(payout.amount || 0).toFixed(2)}
                  </TableCell>
                  <TableCell className="text-center">
                    <Badge className={PAYOUT_STATUS_STYLE[payout.status] || "bg-slate-100 text-slate-700"}>
                      {payout.status}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-sm text-slate-600">
                    {payout.status === "completed" ? (
                      <>
                        {payout.method === "check" && payout.method_details?.check_number
                          ? `Check #${payout.method_details.check_number}`
                          : payout.method === "bank_transfer" ? "Bank transfer" : payout.method || "—"}
                        {(payout.reference_number || payout.method_details?.reference_number) && (
                          <span className="block text-xs text-slate-400">
                            Ref: {payout.reference_number || payout.method_details?.reference_number}
                          </span>
                        )}
                      </>
                    ) : "—"}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button size="sm" variant="ghost" onClick={() => setLedgerPayoutId(payout.payout_id)}>
                      <BookOpen className="w-4 h-4 mr-1" />
                      View ledger
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Earnings Table */}
      <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
        <div className="p-4 border-b">
          <h2 className="font-semibold text-slate-900">Commission History</h2>
          <p className="text-sm text-slate-500">Detailed breakdown of your earnings</p>
        </div>
        
        {earnings.length === 0 ? (
          <div className="text-center py-12">
            <DollarSign className="w-12 h-12 mx-auto text-slate-300 mb-3" />
            <p className="text-slate-500">No earnings yet</p>
            <p className="text-sm text-slate-400">Commissions will appear here when your resellers make sales</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Order</TableHead>
                <TableHead>Reseller</TableHead>
                <TableHead className="text-right">Order Subtotal</TableHead>
                <TableHead className="text-right">Commission</TableHead>
                <TableHead className="text-center">Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {earnings.map((earning, idx) => (
                <TableRow key={idx}>
                  <TableCell className="text-sm text-slate-500">
                    {new Date(earning.created_at).toLocaleDateString()}
                  </TableCell>
                  <TableCell className="font-mono text-sm">
                    #{earning.order_id?.slice(-8)}
                  </TableCell>
                  <TableCell>
                    <p className="font-medium">{earning.store_name}</p>
                  </TableCell>
                  <TableCell className="text-right">
                    ${(earning.order_subtotal || 0).toFixed(2)}
                  </TableCell>
                  <TableCell className="text-right font-medium text-green-600">
                    +${(earning.commission || 0).toFixed(2)}
                  </TableCell>
                  <TableCell className="text-center">
                    <Badge variant={earning.paid ? "default" : earning.in_payout ? "outline" : "secondary"}>
                      {earning.paid ? "Paid" : earning.in_payout ? "In payout" : "Pending"}
                    </Badge>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      {/* Ledger entries behind one of my payouts */}
      <PayoutLedgerDialog
        open={!!ledgerPayoutId}
        onClose={() => setLedgerPayoutId(null)}
        payoutId={ledgerPayoutId}
        payoutKind="distributor"
        viewer="distributor"
      />
    </div>
  );
}
